import { Link } from "@tanstack/react-router";
import React from "react";

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import MuiAppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import { styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';

import MainLinks from "./MainLinks";

import scoreg from "../assets/scoreg.png";
import scoreg_beta from "../assets/scoreg_beta.png";

import { getColorByBgColor } from "@/lib/colors";
import { getLogo, getOrganization, getRole } from "@/store/auth/authSlice";
import { Avatar, Box, Typography } from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useSelector } from "react-redux";
import useResponsive from "./hooks/useResponsive";
import UserAvatar from "./UserAvatar";

export default function Header() {

    const drawerWidth = 240;

    const { xsmall, small, minMedium, minLarge } = useResponsive();
    const [open, setOpen] = React.useState(minMedium);

    const toggleDrawer = () => {
        setOpen(!open);
    };

    const Spacer = styled('div')(() => ({
        position: 'relative',
        width: '100%',
    }));


    const sessionOrganization = useSelector(getOrganization);
    const sessionRole = useSelector(getRole)
    const organizationLogo = useSelector(getLogo);

    const auth = useAuth();


    const width = organizationLogo && organizationLogo.width && organizationLogo.height ? (40 / organizationLogo.height * organizationLogo.width) + 'px' : '40px'
    // if (organizationLogo) {
    // console.log(organizationLogo.width)
    // console.log(organizationLogo.height)
    // console.log(width)
    // }

    return (
        <>
            <MuiAppBar position="fixed" id="toolbar">
                <Toolbar
                    sx={{
                        pr: '24px', // keep right padding when drawer closed
                        pl: { xs: '0px' }
                    }}
                    style={{ backgroundColor: sessionOrganization?.color }}
                >
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        sx={[
                            {
                                marginRight: '36px'
                            },
                            open && { display: 'none' },
                        ]}
                    >
                        <MenuIcon />
                    </IconButton>
                    {(small || xsmall || (minMedium && !open)) &&
                        <Link to="/membershipsOverview"
                            search={{
                                filter: [
                                    { field: "active", operator: "is", value: "true" },
                                    { field: "organization.uuid", operator: "isAnyOf", value: [sessionOrganization.uuid] },
                                    { field: "scoutstate", operator: "isAnyOf", value: ['MEMBER_FULL', 'MEMBER_ADD'] }
                                ],
                                sort: [{ field: "member.scoutId", direction: "asc" }],
                                visibility: [{ field: "properties.joblist" }, { field: "properties.section" }],
                            }}
                            className="[&.active]:font-bold">
                            <img src={import.meta.env.VITE_ENVIRONMENT === 'production' ? scoreg : scoreg_beta} width="150px"></img>
                        </Link>
                    }
                    {
                        /*            
                      <IconButton color="inherit">
                        <Badge badgeContent={4} color="secondary">
                          <NotificationsIcon />
                        </Badge>
                      </IconButton>
                      */
                    }
                    <Spacer />
                    {
                        auth.isAuthenticated && sessionOrganization && sessionOrganization.uuid &&
                        <>
                            <Link
                                to="/organization/$organizationUuid/main"
                                params={{ organizationUuid: sessionOrganization?.uuid }}
                                style={{ color: getColorByBgColor(sessionOrganization?.color) }}
                            >
                                <Typography style={{ whiteSpace: "nowrap" }} sx={{
                                    display: "inline"
                                }}>
                                    <IconButton sx={{ p: 0, pl: 2 }}>
                                        <Avatar src={`data:image/jpeg;base64,${organizationLogo && organizationLogo.base64}`} variant="square" sx={{ width: width, maxWidth: 80 }} >
                                            {sessionOrganization && sessionOrganization.uuid ? (sessionOrganization.name?.charAt(0)) : ""}
                                        </Avatar>
                                    </IconButton>
                                    {minMedium &&
                                        <>
                                            <span>&nbsp;&nbsp;</span>
                                            <span>{sessionOrganization?.name}&nbsp;</span>
                                        </>
                                    }
                                    {minLarge && sessionRole &&
                                        <>
                                            <span>({sessionRole})&nbsp;</span>
                                        </>
                                    }
                                </Typography>
                            </Link>
                            <UserAvatar />
                        </>
                    }

                </Toolbar>
            </MuiAppBar>
            <Box
                component="nav"
                sx={{ width: { xs: 0, sm: 0, md: open ? drawerWidth : 0 }, flexShrink: { sm: 0 } }}>
                <MuiDrawer anchor="left" open={open} onClose={toggleDrawer}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}>
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            px: [1],
                        }}
                    >
                        <IconButton onClick={toggleDrawer}>
                            <ChevronLeftIcon />
                        </IconButton>
                    </Toolbar>
                    <Divider />
                    <List component="nav">
                        <MainLinks toggleDrawer={toggleDrawer} />
                    </List>
                </MuiDrawer>

                <MuiDrawer variant="persistent" open={open} anchor="left"
                    sx={{
                        display: { xs: 'none', sm: 'none', md: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}>
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'left',
                            justifyContent: 'flex-start',
                            px: [1],
                        }}
                        style={{ backgroundColor: 'white' }}
                    >
                        <Link to="/membershipsOverview" search={{
                            filter: [
                                { field: "active", operator: "is", value: "true" },
                                { field: "organization.uuid", operator: "isAnyOf", value: [sessionOrganization.uuid] },
                                { field: "scoutstate", operator: "isAnyOf", value: ['MEMBER_FULL', 'MEMBER_ADD'] }
                            ],
                            sort: [{ field: "member.scoutId", direction: "asc" }],
                            visibility: [{ field: "properties.joblist" }, { field: "properties.section" }],
                        }}
                            className="[&.active]:font-bold" style={{ paddingLeft: '10px' }}>
                            <img src={import.meta.env.VITE_ENVIRONMENT === 'production' ? scoreg : scoreg_beta} width="150px" className="scoreg-logo"></img>
                        </Link>
                        <Spacer />
                        <IconButton onClick={toggleDrawer}>
                            <ChevronLeftIcon />
                        </IconButton>
                    </Toolbar>
                    <Divider />
                    <List component="nav">
                        <MainLinks />
                    </List>
                </MuiDrawer>
            </Box>
        </>
    );
}